import React, { useState, useEffect, useRef } from 'react';
import api from '../api';

export interface ShipDetails {
  delivery_method: 'partner' | 'hand_delivery' | 'custom';
  shipping_provider: string | null;
  awb_number: string | null;
}

interface ShipOrderModalProps {
  isOpen: boolean;
  orderNumber?: string;
  loading?: boolean;
  onConfirm: (details: ShipDetails) => void;
  onCancel: () => void;
}

// Fallback partners if the carriers endpoint is unavailable
const FALLBACK_PARTNERS = ['Delhivery', 'BlueDart', 'DTDC', 'Ekart', 'India Post', 'Xpressbees'];

type Method = 'partner' | 'hand_delivery' | 'custom';

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '8px 12px',
  border: '1px solid #e2e8f0',
  borderRadius: '6px',
  fontSize: '13px',
  boxSizing: 'border-box',
  background: 'white',
};

const labelStyle: React.CSSProperties = {
  fontSize: '12px',
  fontWeight: 600,
  color: '#334155',
  display: 'block',
  marginBottom: '4px',
};

/**
 * Modal shown when moving an order from "allocated" to "shipped".
 * Lets the user pick a delivery method (Partner / Hand delivery / Custom)
 * and manually enter the shipping partner and AWB / tracking number.
 */
const ShipOrderModal: React.FC<ShipOrderModalProps> = ({
  isOpen,
  orderNumber,
  loading = false,
  onConfirm,
  onCancel,
}) => {
  const [method, setMethod] = useState<Method>('partner');
  const [provider, setProvider] = useState('');
  const [awb, setAwb] = useState('');
  const [partners, setPartners] = useState<string[]>(FALLBACK_PARTNERS);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [error, setError] = useState('');
  const providerRef = useRef<HTMLDivElement>(null);

  // Reset the form each time the modal opens
  useEffect(() => {
    if (isOpen) {
      setMethod('partner');
      setProvider('');
      setAwb('');
      setError('');
      setDropdownOpen(false);
      fetchPartners();
    }
  }, [isOpen]);

  // Close the typeahead dropdown when clicking outside it
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (providerRef.current && !providerRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const fetchPartners = async () => {
    try {
      const res = await api.get('/shipping/carriers/');
      const list = Array.isArray(res.data) ? res.data : [];
      const names = list
        .map((c: any) => c?.name || c?.code)
        .filter((n: any): n is string => typeof n === 'string' && n.length > 0);
      if (names.length > 0) {
        // Merge with fallbacks and de-duplicate (case-insensitive)
        const seen = new Set<string>();
        const merged = [...names, ...FALLBACK_PARTNERS].filter((n) => {
          const key = n.toLowerCase();
          if (seen.has(key)) return false;
          seen.add(key);
          return true;
        });
        setPartners(merged);
      }
    } catch {
      // Keep fallback list on failure
      setPartners(FALLBACK_PARTNERS);
    }
  };

  if (!isOpen) return null;

  const isPartner = method === 'partner';
  const isCustom = method === 'custom';
  const isHandDelivery = method === 'hand_delivery';
  // Partner & custom capture a provider; hand delivery does not (the provider
  // is implicitly "Hand Delivery").
  const needsProvider = isPartner || isCustom;
  // Every method now captures an AWB / tracking / reference number, including
  // hand delivery (a delivery reference like a receipt/acknowledgement no.).
  const needsAwb = isPartner || isCustom || isHandDelivery;

  const filteredPartners = partners.filter((p) =>
    p.toLowerCase().includes(provider.toLowerCase())
  );

  const handleConfirm = () => {
    // Validation depends on the chosen method
    if (needsProvider && !provider.trim()) {
      setError(isPartner ? 'Please choose a shipping partner.' : 'Please enter a provider name.');
      return;
    }
    if (needsAwb && !awb.trim()) {
      setError(isHandDelivery ? 'Please enter the delivery reference / tracking number.' : 'Please enter the AWB / tracking number.');
      return;
    }
    setError('');
    onConfirm({
      delivery_method: method,
      // Hand delivery has no explicit partner — record it as "Hand Delivery".
      shipping_provider: needsProvider ? provider.trim() : (isHandDelivery ? 'Hand Delivery' : null),
      awb_number: needsAwb ? awb.trim() : null,
    });
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') onCancel();
  };

  const methodOptions: { value: Method; label: string; hint: string }[] = [
    { value: 'partner', label: 'Partner', hint: 'A registered courier / logistics partner' },
    { value: 'hand_delivery', label: 'Hand Delivery', hint: 'Delivered in person, with a reference no.' },
    { value: 'custom', label: 'Custom', hint: 'Any other provider, entered manually' },
  ];

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
      }}
      onClick={onCancel}
      onKeyDown={handleKeyDown}
    >
      <div
        style={{
          background: 'white',
          borderRadius: '12px',
          padding: '24px',
          maxWidth: '460px',
          width: '90%',
          boxShadow: '0 20px 25px rgba(0,0,0,0.15)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
          <div
            style={{
              width: '36px',
              height: '36px',
              borderRadius: '8px',
              background: '#ccfbf1',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#0d9488" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="1" y="3" width="15" height="13" />
              <polygon points="16 8 20 8 23 11 23 16 16 16 16 8" />
              <circle cx="5.5" cy="18.5" r="2.5" />
              <circle cx="18.5" cy="18.5" r="2.5" />
            </svg>
          </div>
          <div>
            <h3 style={{ fontSize: '16px', fontWeight: 700, color: '#1e293b', margin: 0 }}>
              Ship Order
            </h3>
            {orderNumber && (
              <div style={{ fontSize: '12px', color: '#64748b', marginTop: '2px' }}>
                Order {orderNumber}
              </div>
            )}
          </div>
        </div>

        <p style={{ fontSize: '13px', color: '#475569', lineHeight: 1.6, margin: '0 0 16px 0' }}>
          Choose how this order is being shipped and add the tracking details.
        </p>

        {/* Delivery method selector */}
        <div style={{ marginBottom: '16px' }}>
          <label style={labelStyle}>Delivery Method</label>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            {methodOptions.map((opt) => {
              const active = method === opt.value;
              return (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => {
                    setMethod(opt.value);
                    setError('');
                  }}
                  title={opt.hint}
                  style={{
                    flex: '1 1 0',
                    minWidth: '120px',
                    padding: '10px 12px',
                    borderRadius: '8px',
                    border: active ? '1px solid #0d9488' : '1px solid #e2e8f0',
                    background: active ? '#f0fdfa' : 'white',
                    color: active ? '#0f766e' : '#334155',
                    fontSize: '13px',
                    fontWeight: 600,
                    cursor: 'pointer',
                    textAlign: 'left',
                    boxShadow: active ? '0 0 0 2px rgba(13, 148, 136, 0.1)' : 'none',
                    transition: '150ms cubic-bezier(0.4, 0, 0.2, 1)',
                  }}
                >
                  <div>{opt.label}</div>
                  <div style={{ fontSize: '11px', fontWeight: 400, color: '#94a3b8', marginTop: '2px' }}>
                    {opt.hint}
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Partner / provider field */}
        {needsProvider && (
          <div style={{ marginBottom: '16px' }} ref={providerRef}>
            <label style={labelStyle}>
              {isPartner ? 'Shipping Partner' : 'Provider Name'}
            </label>
            <div style={{ position: 'relative' }}>
              <input
                type="text"
                value={provider}
                placeholder={isPartner ? 'Type to search partners...' : 'Enter provider name'}
                onChange={(e) => {
                  setProvider(e.target.value);
                  setDropdownOpen(true);
                }}
                onFocus={() => setDropdownOpen(true)}
                style={inputStyle}
              />
              {isPartner && dropdownOpen && filteredPartners.length > 0 && (
                <div
                  style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    zIndex: 100,
                    background: 'white',
                    border: '1px solid #e2e8f0',
                    borderRadius: '6px',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                    maxHeight: '200px',
                    overflow: 'auto',
                    marginTop: '2px',
                  }}
                >
                  {filteredPartners.map((p, idx) => (
                    <div
                      key={idx}
                      onMouseDown={() => {
                        setProvider(p);
                        setDropdownOpen(false);
                      }}
                      style={{
                        padding: '8px 12px',
                        cursor: 'pointer',
                        fontSize: '13px',
                        borderBottom: '1px solid #f1f5f9',
                      }}
                      onMouseOver={(e) => (e.currentTarget.style.background = '#f1f5f9')}
                      onMouseOut={(e) => (e.currentTarget.style.background = 'white')}
                    >
                      {p}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* AWB / tracking / reference field — required for every method */}
        {needsAwb && (
          <div style={{ marginBottom: '16px' }}>
            <label style={labelStyle}>
              {isHandDelivery ? 'Delivery Reference / Tracking Number' : 'AWB / Tracking Number'}
            </label>
            <input
              type="text"
              value={awb}
              placeholder={isHandDelivery ? 'Enter delivery reference number' : 'Enter AWB number'}
              onChange={(e) => setAwb(e.target.value)}
              style={{ ...inputStyle, fontFamily: "'SF Mono', 'Fira Code', monospace" }}
            />
          </div>
        )}

        {/* Hand delivery note */}
        {isHandDelivery && (
          <div
            style={{
              padding: '8px 12px',
              background: '#f8f9fa',
              border: '1px solid #e2e8f0',
              borderRadius: '6px',
              fontSize: '12px',
              color: '#64748b',
              marginBottom: '16px',
            }}
          >
            Delivered in person. Enter a delivery reference / tracking number for traceability.
          </div>
        )}

        {/* Error */}
        {error && (
          <div
            style={{
              padding: '8px 12px',
              background: '#fef2f2',
              border: '1px solid #fca5a5',
              borderRadius: '6px',
              fontSize: '12px',
              fontWeight: 600,
              color: '#991b1b',
              marginBottom: '16px',
            }}
          >
            {error}
          </div>
        )}

        {/* Actions */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '4px' }}>
          <button
            type="button"
            onClick={onCancel}
            disabled={loading}
            style={{
              padding: '6px 14px',
              fontSize: '13px',
              fontWeight: 600,
              borderRadius: '6px',
              border: '1px solid #e2e8f0',
              background: 'white',
              color: '#334155',
              cursor: loading ? 'not-allowed' : 'pointer',
              opacity: loading ? 0.6 : 1,
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={loading}
            style={{
              padding: '6px 14px',
              fontSize: '13px',
              fontWeight: 600,
              borderRadius: '6px',
              border: 'none',
              background: '#0d9488',
              color: 'white',
              cursor: loading ? 'not-allowed' : 'pointer',
              opacity: loading ? 0.6 : 1,
            }}
          >
            {loading ? 'Shipping...' : 'Confirm & Ship'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ShipOrderModal;
